/*
 * APS Shipping Protection (replaces the Navidium app)
 * Artistic Painting Studio, Dawn 11 theme.
 *
 * Keeps exactly one "Shipping Protection" line in the cart, at today's prices:
 *   price = the cheapest protection variant that is at least $0.75 and at least
 *           2.5% of the cart, capped at the most expensive variant ($74.25).
 *   "The cart" = every other line after its own discounts, minus its share of any
 *   discount on the whole order (like Navidium, which read the cart total). The
 *   protection line itself is never counted.
 *
 * No protection when the customer switched it off, when nothing in the cart
 * needs shipping (finish formulas, videos, eBooks), when the cart has no
 * other items, outside the countries set in the snippet (US only, as with
 * Navidium), or when aps_enabled is false in the snippet.
 *
 * Every change to the protection line goes through /cart/change.js (by line key)
 * and /cart/add.js, which Shopify documents for exactly this.
 *
 * The texts, the link and the product live in snippets/aps-protection.liquid.
 * Nothing in this file needs editing.
 */
(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof window !== 'undefined' && window.document) {
    if (window.APSProtection && window.APSProtection.version) return; // loaded twice: keep the first copy
    window.APSProtection = api;
    api.boot(window, window.document);
  } else if (typeof module === 'object' && module && module.exports) {
    module.exports = api;
  }
})(this, function () {
  'use strict';

  var VERSION = '1.1.0';
  var OWN_HEADER = 'X-APS-Protection';
  var CART_MUTATION_PATH = /\/cart\/(add|change|update|clear)(\.js|\.json)?\/?$/i;

  var DEBOUNCE_MS = 400; // wait this long after the last cart change before checking
  var MAX_WAIT_MS = 8000; // never wait longer than this for other cart requests to finish
  var MAX_STEPS = 4; // cart changes in one check (normally 1; leftovers need 2), then check again
  var GUARD_WINDOW_MS = 30000;
  var GUARD_MAX = 30; // at most 30 cart changes by this script in 30 seconds
  var GUARD_MAX_SAME = 4; // the same change 4 times in 30 seconds means something keeps undoing it
  var CHECKOUT_WAIT_MS = 4000;

  // ---------------------------------------------------------------- helpers

  function num(value) {
    var n = Number(value);
    return isFinite(n) ? n : 0;
  }

  function assign(target) {
    for (var i = 1; i < arguments.length; i++) {
      var src = arguments[i];
      if (!src) continue;
      for (var k in src) if (Object.prototype.hasOwnProperty.call(src, k)) target[k] = src[k];
    }
    return target;
  }

  function warn(message, err) {
    try {
      if (typeof console !== 'undefined' && console.warn) console.warn('[APS Shipping Protection] ' + message, err || '');
    } catch (e) {
      /* ignore */
    }
  }

  // ---------------------------------------------------------------- settings

  function normalizeConfig(raw) {
    if (!raw || typeof raw !== 'object' || !raw.handle) return null;
    var variants = (raw.variants || [])
      .map(function (v) {
        return { id: Number(v && v.id), price: Math.round(num(v && v.price)) };
      })
      .filter(function (v) {
        return v.id > 0 && v.price >= 0;
      });
    if (!variants.length) return null;
    return {
      enabled: raw.enabled !== false,
      handle: String(raw.handle),
      productId: raw.productId ? String(raw.productId) : '',
      variants: variants,
      minCents: raw.minCents == null ? 75 : Math.round(num(raw.minCents)),
      ratePerMille: raw.ratePerMille == null ? 25 : num(raw.ratePerMille),
      attribute: String(raw.attribute || 'Shipping Protection'),
      declinedValue: String(raw.declinedValue || 'Declined'),
      disclaimerName: String(raw.disclaimerName || ''),
      disclaimerValue: String(raw.disclaimerValue || ''),
      moneyFormat: String(raw.moneyFormat || '${{amount}}'),
      cartCount: raw.cartCount == null ? null : num(raw.cartCount),
    };
  }

  // ---------------------------------------------------------------- pricing

  /**
   * The protection variant for a cart worth `baseCents`:
   * the cheapest variant priced at least `minCents` and at least
   * ratePerMille/1000 of the cart; if none is enough, the most expensive one.
   * Everything is in whole cents, so there is no rounding error.
   * snippets/aps-protection.liquid does exactly the same in Liquid.
   */
  function pickVariant(baseCents, cfg) {
    var need = Math.max(0, Math.round(num(baseCents))) * cfg.ratePerMille;
    var best = null;
    var max = null;
    for (var i = 0; i < cfg.variants.length; i++) {
      var v = cfg.variants[i];
      if (!max || v.price > max.price) max = v;
      if (v.price >= cfg.minCents && v.price * 1000 >= need && (!best || v.price < best.price)) best = v;
    }
    return best || max;
  }

  // ---------------------------------------------------------------- the cart

  function isProtectionItem(item, cfg) {
    if (!item) return false;
    if (cfg.handle && item.handle === cfg.handle) return true;
    return !!cfg.productId && String(item.product_id) === cfg.productId;
  }

  function lineVariantId(item) {
    return Number(item.variant_id || item.id);
  }

  function lineTotal(item) {
    if (item.final_line_price != null) return Math.round(num(item.final_line_price));
    if (item.line_price != null) return Math.round(num(item.line_price));
    return Math.round(num(item.price) * num(item.quantity));
  }

  function isDeclined(cart, cfg) {
    var attrs = cart && cart.attributes;
    return !!attrs && attrs[cfg.attribute] === cfg.declinedValue;
  }

  function disclaimerProps(cfg) {
    if (!cfg.disclaimerName || !cfg.disclaimerValue) return null;
    var props = {};
    props[cfg.disclaimerName] = cfg.disclaimerValue;
    return props;
  }

  function hasDisclaimer(item, cfg) {
    if (!cfg.disclaimerName || !cfg.disclaimerValue) return true;
    return !!item.properties && item.properties[cfg.disclaimerName] === cfg.disclaimerValue;
  }

  /** Discounts on the whole order (cart-level codes and automatic discounts), in cents. */
  function orderDiscount(cart) {
    var apps = (cart && cart.cart_level_discount_applications) || [];
    var total = 0;
    for (var i = 0; i < apps.length; i++) total += Math.max(0, Math.round(num(apps[i] && apps[i].total_allocated_amount)));
    return total;
  }

  /**
   * The cart value the price is based on: every other line after its own discounts,
   * minus that lines' share of any order discount. Shopify spreads an order discount
   * over all lines, the protection line included, so only the other lines' share is
   * taken off: discount x others / (others + protection). For a percentage discount
   * that is exactly (100% - discount) of the other lines, whatever the protection
   * costs, so the price can't flip back and forth. Whole cents, rounded down,
   * exactly as snippets/aps-protection.liquid does it.
   */
  function cartValue(goods, protectionTotal, discount) {
    if (discount <= 0 || goods <= 0) return goods;
    var share = Math.floor((discount * goods) / (goods + Math.max(0, protectionTotal)));
    return Math.max(0, goods - share);
  }

  function summarize(cart, cfg) {
    var items = (cart && cart.items) || [];
    var protection = [];
    var others = [];
    var goods = 0;
    var protectionTotal = 0;
    var shippable = false;
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (isProtectionItem(item, cfg)) {
        protection.push(item);
        protectionTotal += lineTotal(item);
      } else {
        others.push(item);
        goods += lineTotal(item);
        if (item.requires_shipping) shippable = true;
      }
    }
    var base = cartValue(goods, protectionTotal, orderDiscount(cart));
    var declined = isDeclined(cart, cfg);
    var eligible = cfg.enabled !== false && others.length > 0 && shippable;
    return {
      protection: protection,
      others: others,
      goods: goods,
      base: base,
      shippable: shippable,
      declined: declined,
      eligible: eligible,
      wanted: eligible && !declined,
      target: pickVariant(base, cfg),
    };
  }

  function keysOf(lines) {
    return lines.map(function (p) {
      return String(p.key);
    });
  }

  /**
   * What (if anything) the cart needs so that it holds exactly the right
   * protection line. Returns one step; after it is done the cart is checked again.
   *   none   nothing to do
   *   remove take every protection line out (switched off / nothing to ship / no other items)
   *   add    add the right variant (with the Disclaimer)
   *   dedupe more than one protection line: remove the extras
   *   swap   wrong price: remove the old line, then add the right variant (with the Disclaimer)
   *   fix    right variant, but quantity is not 1 or the Disclaimer is missing
   */
  function decide(cart, cfg) {
    var s = summarize(cart, cfg);
    if (!s.wanted) {
      if (!s.protection.length) return { type: 'none', summary: s };
      return { type: 'remove', keys: keysOf(s.protection), summary: s };
    }
    var target = s.target;
    if (!target) return { type: 'none', summary: s };
    if (!s.protection.length) {
      return { type: 'add', variantId: target.id, properties: disclaimerProps(cfg), summary: s };
    }
    var keeper =
      find(s.protection, function (p) {
        return lineVariantId(p) === target.id && p.quantity === 1 && hasDisclaimer(p, cfg);
      }) ||
      find(s.protection, function (p) {
        return lineVariantId(p) === target.id;
      }) ||
      s.protection[0];
    var extras = s.protection.filter(function (p) {
      return p !== keeper;
    });
    if (extras.length) {
      return { type: 'dedupe', keys: keysOf(extras), summary: s };
    }
    if (lineVariantId(keeper) !== target.id) {
      return {
        type: 'swap',
        key: String(keeper.key),
        fromVariantId: lineVariantId(keeper),
        variantId: target.id,
        properties: disclaimerProps(cfg),
        summary: s,
      };
    }
    if (keeper.quantity !== 1 || !hasDisclaimer(keeper, cfg)) {
      return { type: 'fix', key: String(keeper.key), variantId: target.id, properties: disclaimerProps(cfg), summary: s };
    }
    return { type: 'none', summary: s };
  }

  function find(list, test) {
    for (var i = 0; i < list.length; i++) if (test(list[i])) return list[i];
    return null;
  }

  /** Names the exact change (which lines, which variant), so the loop guard only counts true repeats. */
  function signature(d) {
    if (d.type === 'swap') return 'swap:' + d.key + '>' + d.variantId;
    if (d.type === 'add') return 'add:' + d.variantId;
    if (d.type === 'fix') return 'fix:' + d.key;
    return d.type + ':' + (d.keys || []).slice().sort().join(',');
  }

  // ---------------------------------------------------------------- money

  function formatMoney(cents, format) {
    var fmt = String(format || '${{amount}}').replace(/<[^>]*>/g, '');
    var match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var value = Math.round(num(cents));
    function group(decimals, thousands, decimal) {
      var parts = (value / 100).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
    }
    var out;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals':
        out = group(0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        out = group(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = group(0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        out = group(2, "'", '.');
        break;
      case 'amount_with_space_separator':
        out = group(2, ' ', ',');
        break;
      default:
        out = group(2, ',', '.');
    }
    return match ? fmt.replace(match[0], out) : out;
  }

  // ---------------------------------------------------------------- spotting cart changes

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input === 'object') return input.url || input.href || '';
    return input == null ? '' : String(input);
  }

  function isCartMutationUrl(url, base) {
    if (!url) return false;
    var path;
    try {
      var u = new URL(String(url), base || 'https://shop.invalid/');
      if (base && u.origin !== new URL(base).origin) return false;
      path = u.pathname;
    } catch (e) {
      path = String(url).split(/[?#]/)[0];
    }
    return CART_MUTATION_PATH.test(path);
  }

  function headerValue(headers, name) {
    if (!headers) return null;
    var lower = name.toLowerCase();
    if (typeof headers.get === 'function') return headers.get(name);
    if (Array.isArray(headers)) {
      for (var i = 0; i < headers.length; i++) {
        if (headers[i] && String(headers[i][0]).toLowerCase() === lower) return headers[i][1];
      }
      return null;
    }
    for (var k in headers) {
      if (Object.prototype.hasOwnProperty.call(headers, k) && k.toLowerCase() === lower) return headers[k];
    }
    return null;
  }

  function isOwnRequest(input, init) {
    if (headerValue(init && init.headers, OWN_HEADER)) return true;
    return !!(input && typeof input === 'object' && headerValue(input.headers, OWN_HEADER));
  }

  /** Watch fetch() for cart changes made by the theme or other apps (our own calls are marked and skipped). */
  function installFetchHook(win, ctl) {
    var original = win.fetch;
    if (typeof original !== 'function' || original.__apsProtection) return false;
    var wrapped = function (input, init) {
      var foreign = false;
      try {
        foreign = isCartMutationUrl(requestUrl(input), win.location && win.location.href) && !isOwnRequest(input, init);
      } catch (e) {
        foreign = false;
      }
      if (!foreign) return original.apply(win, arguments);
      ctl.foreignStart();
      var result;
      try {
        result = original.apply(win, arguments);
      } catch (e) {
        ctl.foreignEnd();
        throw e;
      }
      Promise.resolve(result).then(
        function () {
          ctl.foreignEnd();
        },
        function () {
          ctl.foreignEnd();
        }
      );
      return result;
    };
    wrapped.__apsProtection = true;
    win.fetch = wrapped;
    return true;
  }

  /** Same for XMLHttpRequest (jQuery and older app scripts). */
  function installXhrHook(win, ctl) {
    var X = win.XMLHttpRequest;
    if (!X || !X.prototype || X.prototype.__apsProtection) return false;
    var open = X.prototype.open;
    var send = X.prototype.send;
    X.prototype.open = function (method, url) {
      try {
        this.__apsCartChange = isCartMutationUrl(requestUrl(url), win.location && win.location.href);
      } catch (e) {
        this.__apsCartChange = false;
      }
      return open.apply(this, arguments);
    };
    X.prototype.send = function () {
      if (this.__apsCartChange) {
        var ended = false;
        var end = function () {
          if (ended) return;
          ended = true;
          ctl.foreignEnd();
        };
        ctl.foreignStart();
        this.addEventListener('loadend', end);
        try {
          return send.apply(this, arguments);
        } catch (e) {
          end();
          throw e;
        }
      }
      return send.apply(this, arguments);
    };
    X.prototype.__apsProtection = true;
    return true;
  }

  // ---------------------------------------------------------------- loop guard

  function createGuard(now) {
    var log = [];
    var tripped = false;
    return {
      allow: function (sig) {
        if (tripped) return false;
        var t = now();
        log = log.filter(function (e) {
          return t - e.t < GUARD_WINDOW_MS;
        });
        var same = log.filter(function (e) {
          return e.sig === sig;
        }).length;
        if (log.length >= GUARD_MAX || same >= GUARD_MAX_SAME) {
          tripped = true;
          return false;
        }
        log.push({ t: t, sig: sig });
        return true;
      },
      reset: function () {
        tripped = false;
        log = [];
      },
      isTripped: function () {
        return tripped;
      },
    };
  }

  // ---------------------------------------------------------------- controller

  /**
   * env: config, fetch(url, init), root ('/' or '/en-ca/'), sectionIds(), sectionsUrl(),
   *      render(result), busy(on), hold(on) (optional), now(), setTimeout(fn, ms), clearTimeout(id), warn(msg, err)
   *
   * render(result) gets { cart, changed, sections, failed, paused, ... }. When result.sections
   * is set, it shows the cart as it is now: no other app changed the cart after it was made.
   */
  function createController(env) {
    var cfg = env.config;
    var guard = createGuard(env.now);
    var queue = Promise.resolve();
    var queued = 0;
    var timer = null;
    var waitingSince = 0;
    var inFlight = 0;
    var again = false;
    var lastCart = null;
    var warnedGuard = false;
    var cleanedUp = false;
    var sectionsStale = false; // the drawer / cart page may show an older cart than Shopify has
    var holding = false;
    var idleWaiters = [];
    var stats = { requests: 0, mutations: 0, passes: 0, foreign: 0 };
    var log = env.warn || warn;

    function request(path, body, absolute) {
      var headers = { Accept: 'application/json' };
      headers[OWN_HEADER] = '1';
      var init = { method: body ? 'POST' : 'GET', headers: headers, credentials: 'same-origin' };
      if (body) {
        headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
      }
      stats.requests++;
      return Promise.resolve(env.fetch(absolute ? path : env.root + path, init)).then(function (res) {
        return res.text().then(function (text) {
          var json = null;
          try {
            json = text ? JSON.parse(text) : null;
          } catch (e) {
            json = null;
          }
          if (!res.ok || !json) {
            var err = new Error((json && (json.description || json.message)) || 'HTTP ' + res.status);
            err.status = res.status;
            throw err;
          }
          return json;
        });
      });
    }

    function getCart() {
      return request('cart.js');
    }

    function withSections(body, wanted) {
      if (wanted === false) return body;
      var ids = env.sectionIds() || [];
      if (ids.length) {
        body.sections = ids;
        body.sections_url = env.sectionsUrl();
      }
      return body;
    }

    /** The drawer / cart page on their own (Shopify's Section Rendering API). */
    function fetchSections() {
      var ids = env.sectionIds() || [];
      if (!ids.length) return Promise.resolve({});
      return request(env.sectionsUrl() + '?sections=' + encodeURIComponent(ids.join(',')), null, true).catch(function () {
        return null;
      });
    }

    /** "No other app has changed the cart since now": compared with stats.foreign later. -1 while another app's request runs. */
    function mark() {
      return inFlight > 0 ? -1 : stats.foreign;
    }

    function removeLine(key, wantSections) {
      stats.mutations++;
      return request('cart/change.js', withSections({ id: key, quantity: 0 }, wantSections));
    }

    function addLine(variantId, properties) {
      stats.mutations++;
      var item = { id: variantId, quantity: 1 };
      if (properties) item.properties = properties;
      return request('cart/add.js', withSections({ items: [item] })).then(function (r) {
        return { cart: null, sections: r.sections || null };
      });
    }

    function mutate(d) {
      if (d.type === 'add') return addLine(d.variantId, d.properties);
      if (d.type === 'swap') {
        // Take the old line out by its key, then add the right variant with the Disclaimer.
        return removeLine(d.key, false).then(function () {
          return addLine(d.variantId, d.properties);
        });
      }
      if (d.type === 'fix') {
        stats.mutations++;
        var body = { id: d.key, quantity: 1 };
        if (d.properties) body.properties = d.properties;
        return request('cart/change.js', withSections(body)).then(function (r) {
          return { cart: r, sections: r.sections || null };
        });
      }
      // remove, dedupe: one /cart/change.js per line, by its key
      var keys = d.keys || [];
      var chain = Promise.resolve(null);
      keys.forEach(function (key, i) {
        chain = chain.then(function () {
          return removeLine(key, i === keys.length - 1);
        });
      });
      return chain.then(function (r) {
        return { cart: r, sections: (r && r.sections) || null };
      });
    }

    /** Check the cart and change it (at most MAX_STEPS times) until it holds the right protection line. */
    function pass(startCart) {
      stats.passes++;
      var result = { cart: null, changed: false, sections: null, sectionsMark: -1, stopped: false, paused: false, failed: false, decision: 'none' };
      var steps = 0;

      function apply(d) {
        env.busy(true);
        var m = mark();
        return mutate(d).then(
          function (r) {
            result.changed = true;
            if (r.sections) {
              result.sections = r.sections;
              result.sectionsMark = m;
            }
            return r.cart ? r.cart : getCart();
          },
          function (err) {
            result.changed = true; // part of it may have gone through (a swap is two requests): redraw from a fresh read
            throw err;
          }
        );
      }

      /** Paused with two or more protection lines: never let checkout charge twice, so take them all out (once). */
      function cleanUp(s) {
        if (cleanedUp || !s || s.protection.length < 2) return result;
        cleanedUp = true;
        return apply({ type: 'remove', keys: keysOf(s.protection) }).then(function (cart) {
          result.cart = cart;
          lastCart = cart;
          return result;
        });
      }

      function step(cart) {
        result.cart = cart;
        lastCart = cart;
        var d = decide(cart, cfg);
        result.decision = d.type;
        if (d.type === 'none') return result;
        if (steps >= MAX_STEPS) {
          result.stopped = true; // finish() checks again
          return result;
        }
        if (!guard.allow(signature(d))) {
          result.stopped = true;
          result.paused = true;
          if (!warnedGuard) {
            warnedGuard = true;
            log('paused: something keeps changing the protection line back (' + d.type + '). Reload the page to try again.');
          }
          return cleanUp(d.summary);
        }
        steps++;
        return apply(d).then(step);
      }

      return (startCart ? Promise.resolve(startCart) : getCart()).then(step).catch(function (err) {
        // Keep what already worked (result.changed, result.sections): finish() still redraws the cart.
        result.failed = true;
        result.error = err;
        if (!result.cart) result.cart = lastCart;
        log('could not update the cart', err);
        return result;
      });
    }

    /**
     * After a check, make sure the drawer and cart page will show the cart as it is now.
     * Sections from our own request are used only if no other app changed the cart since it
     * was sent. Otherwise they are fetched again once nothing else is running, or by the next
     * check when one is already coming (that check redraws even if it changes nothing).
     */
    function finish(result) {
      if (result.stopped && !result.paused) again = true; // stopped after MAX_STEPS: look again
      var refresh =
        result.failed && result.changed
          ? getCart().then(
              function (c) {
                result.cart = c;
                lastCart = c;
              },
              function () {}
            )
          : Promise.resolve();
      return refresh.then(function () {
        if (!result.changed && !sectionsStale) return result;
        if (result.sections && !result.failed && result.sectionsMark === stats.foreign && inFlight === 0) {
          sectionsStale = false;
          return result;
        }
        result.sections = null;
        if (inFlight > 0 || again) {
          sectionsStale = true;
          return result;
        }
        var m = mark();
        return fetchSections().then(function (sections) {
          if (sections && m === stats.foreign && inFlight === 0) {
            result.sections = sections;
            sectionsStale = false;
          } else {
            sectionsStale = true;
          }
          return result;
        });
      });
    }

    function check(startCart) {
      return pass(startCart).then(finish);
    }

    function isIdle() {
      return !timer && queued === 0 && inFlight === 0;
    }

    /** Tell the page when a check is pending or running (it holds the express checkout buttons meanwhile). */
    function syncHold() {
      var on = !isIdle();
      if (on === holding) return;
      holding = on;
      if (typeof env.hold === 'function') {
        try {
          env.hold(on);
        } catch (e) {
          /* ignore */
        }
      }
    }

    function settleIdle() {
      syncHold();
      if (!isIdle()) return;
      var waiters = idleWaiters;
      idleWaiters = [];
      waiters.forEach(function (fn) {
        fn();
      });
    }

    function enqueue(task) {
      queued++;
      syncHold();
      var p = queue
        .then(task)
        .then(
          function (result) {
            if (result) {
              try {
                env.render(result);
              } catch (e) {
                log('could not refresh the cart display', e);
              }
            }
          },
          function (err) {
            log('could not update the cart', err);
            try {
              env.render({ cart: lastCart, changed: false, failed: true });
            } catch (e) {
              /* ignore */
            }
          }
        )
        .then(function () {
          queued--;
          if (queued === 0) env.busy(false);
          if (again && queued === 0) {
            again = false;
            schedule('again');
          }
          settleIdle();
        });
      queue = p;
      return p;
    }

    function fire() {
      timer = null;
      if (inFlight > 0 && env.now() - waitingSince < MAX_WAIT_MS) {
        timer = env.setTimeout(fire, DEBOUNCE_MS);
        return;
      }
      waitingSince = 0;
      enqueue(function () {
        return check();
      });
    }

    function schedule() {
      if (queued > 0) {
        again = true;
        return;
      }
      if (timer) env.clearTimeout(timer);
      if (!waitingSince) waitingSince = env.now();
      timer = env.setTimeout(fire, DEBOUNCE_MS);
      syncHold();
    }

    /** The customer flipped the switch. The choice is saved on the cart, so it lasts for this cart. */
    function setDeclined(declined) {
      guard.reset();
      warnedGuard = false;
      cleanedUp = false;
      if (timer) {
        env.clearTimeout(timer);
        timer = null;
        waitingSince = 0;
      }
      return enqueue(function () {
        var attrs = {};
        attrs[cfg.attribute] = declined ? cfg.declinedValue : '';
        env.busy(true);
        stats.mutations++;
        var m = mark();
        return request('cart/update.js', withSections({ attributes: attrs })).then(function (cart) {
          return pass(cart).then(function (r) {
            if (!r.sections && cart.sections) {
              r.sections = cart.sections;
              r.sectionsMark = m;
            }
            r.changed = true;
            return finish(r);
          });
        });
      });
    }

    function whenIdle(ms) {
      if (isIdle()) return Promise.resolve(true);
      return new Promise(function (resolve) {
        var done = false;
        var t = env.setTimeout(function () {
          if (done) return;
          done = true;
          resolve(false);
        }, ms);
        idleWaiters.push(function () {
          if (done) return;
          done = true;
          env.clearTimeout(t);
          resolve(true);
        });
      });
    }

    return {
      schedule: schedule,
      setDeclined: setDeclined,
      runNow: function () {
        return enqueue(function () {
          return check();
        });
      },
      foreignStart: function () {
        stats.foreign++;
        inFlight++;
        syncHold();
      },
      foreignEnd: function () {
        inFlight = Math.max(0, inFlight - 1);
        schedule();
      },
      isIdle: isIdle,
      whenIdle: whenIdle,
      isPaused: guard.isTripped,
      isStale: function () {
        return sectionsStale;
      },
      lastCart: function () {
        return lastCart;
      },
      stats: stats,
    };
  }

  // ---------------------------------------------------------------- page (Dawn) wiring

  function shopRoot(win) {
    var r = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  function readConfig(doc) {
    var el = doc.querySelector('script[data-aps-protection-config]');
    if (!el) return null;
    try {
      return normalizeConfig(JSON.parse(el.textContent));
    } catch (e) {
      warn('the settings in snippets/aps-protection.liquid could not be read', e);
      return null;
    }
  }

  function createDomUI(win, doc, cfg) {
    var parser = null;

    function parse(html) {
      parser = parser || new win.DOMParser();
      return parser.parseFromString(String(html || ''), 'text/html');
    }

    function replaceInner(target, source) {
      if (target && source) target.innerHTML = source.innerHTML;
    }

    function sectionIds() {
      var ids = [];
      if (doc.querySelector('cart-drawer')) ids.push('cart-drawer');
      if (doc.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
      var items = doc.getElementById('main-cart-items');
      if (items && items.dataset.id) ids.push(items.dataset.id);
      var footer = doc.getElementById('main-cart-footer');
      if (footer && footer.dataset.id) ids.push(footer.dataset.id);
      return ids.slice(0, 5);
    }

    function busy(on) {
      ['main-cart-items', 'CartDrawer-CartItems'].forEach(function (id) {
        var el = doc.getElementById(id);
        if (el) el.classList.toggle('aps-protection-busy', !!on);
      });
    }

    /**
     * Shop Pay / PayPal buttons on the cart page start checkout straight from Shopify's cart,
     * so the Check out hold can't catch them. Make them unclickable while a check is pending.
     */
    function hold(on) {
      doc.querySelectorAll('.cart__dynamic-checkout-buttons, shopify-accelerated-checkout-cart').forEach(function (el) {
        el.classList.toggle('aps-protection-busy', !!on);
        if (on) el.setAttribute('aria-disabled', 'true');
        else el.removeAttribute('aria-disabled');
      });
    }

    function setChoice(el, on) {
      var input = el.querySelector('[data-aps-protection-input]');
      if (input) input.checked = on;
      var off = el.querySelector('[data-aps-protection-off]');
      if (off) off.hidden = on;
      el.classList.toggle('is-off', !on);
    }

    function showChoice(on) {
      doc.querySelectorAll('[data-aps-protection]').forEach(function (el) {
        setChoice(el, on);
      });
    }

    function updateSwitches(cart, result) {
      var s = summarize(cart, cfg);
      var priceText = s.target ? formatMoney(s.target.price, cfg.moneyFormat) : '';
      // Protection wanted but not in the cart (a request failed, or paused): don't show a price that isn't charged.
      var broken = !!(result && (result.failed || result.paused) && s.wanted && !s.protection.length);
      doc.querySelectorAll('[data-aps-protection]').forEach(function (el) {
        el.hidden = !s.eligible || broken;
        setChoice(el, !s.declined);
        el.querySelectorAll('[data-aps-protection-price]').forEach(function (p) {
          if (priceText && p.textContent !== priceText) p.textContent = priceText;
        });
      });
    }

    function focusedPlace() {
      var a = doc.activeElement;
      if (!a || !a.matches || !a.matches('[data-aps-protection-input]')) return null;
      var wrap = a.closest('[data-aps-protection]');
      return wrap ? wrap.getAttribute('data-aps-protection-place') || '' : '';
    }

    function restoreFocus(place) {
      if (place == null) return;
      var a = doc.activeElement;
      if (a && a.matches && a.matches('[data-aps-protection-input]')) return;
      var el = doc.querySelector('[data-aps-protection-place="' + place + '"] [data-aps-protection-input]');
      if (!el || el.closest('[hidden]')) return;
      try {
        el.focus({ preventScroll: true });
      } catch (e) {
        el.focus();
      }
    }

    function replaceDrawer(drawer, html) {
      var target = doc.getElementById('CartDrawer');
      var source = parse(html).querySelector('#CartDrawer');
      if (!target || !source) return;
      target.innerHTML = source.innerHTML;
      var overlay = target.querySelector('#CartDrawer-Overlay');
      if (overlay && typeof drawer.close === 'function') overlay.addEventListener('click', drawer.close.bind(drawer));
    }

    function applySections(sections, cart) {
      var empty = !cart || !cart.item_count;
      var bubbleDone = false;
      var drawer = doc.querySelector('cart-drawer');
      if (drawer && sections['cart-drawer']) {
        drawer.classList.toggle('is-empty', empty);
        var open = drawer.classList.contains('active');
        if (open && typeof drawer.renderContents === 'function' && sections['cart-icon-bubble']) {
          try {
            drawer.renderContents({ id: null, sections: sections });
            bubbleDone = true;
          } catch (e) {
            replaceDrawer(drawer, sections['cart-drawer']);
          }
        } else {
          replaceDrawer(drawer, sections['cart-drawer']);
        }
      }
      if (!bubbleDone && sections['cart-icon-bubble']) {
        replaceInner(doc.getElementById('cart-icon-bubble'), parse(sections['cart-icon-bubble']).querySelector('.shopify-section'));
      }
      var items = doc.getElementById('main-cart-items');
      if (items && items.dataset.id && sections[items.dataset.id]) {
        replaceInner(items.querySelector('.js-contents'), parse(sections[items.dataset.id]).querySelector('.js-contents'));
        var cartItems = items.closest('cart-items') || doc.querySelector('cart-items');
        if (cartItems) cartItems.classList.toggle('is-empty', empty);
      }
      var footer = doc.getElementById('main-cart-footer');
      if (footer && footer.dataset.id && sections[footer.dataset.id]) {
        replaceInner(footer.querySelector('.js-contents'), parse(sections[footer.dataset.id]).querySelector('.js-contents'));
        footer.classList.toggle('is-empty', empty);
      }
    }

    /** result.sections (when set) are fresh: the controller made sure no other app changed the cart since. */
    function render(result) {
      if (!result) return;
      var cart = result.cart;
      var focus = focusedPlace();
      if (result.sections && cart) applySections(result.sections, cart);
      if (cart) updateSwitches(cart, result);
      restoreFocus(focus);
    }

    return { sectionIds: sectionIds, busy: busy, hold: hold, render: render, showChoice: showChoice, updateSwitches: updateSwitches };
  }

  /**
   * After a quantity change, Dawn moves focus to the first ".cart-item__name" on the page. The
   * hidden protection row has an empty one (without it Dawn shows "There was an error while
   * updating your cart" when the last product is removed). Send that focus to the first real
   * product instead, so keyboard users keep their place.
   */
  function patchTrapFocus(win, doc) {
    var original = win.trapFocus;
    if (typeof original !== 'function' || original.__apsProtection) return false;
    var wrapped = function (container, elementToFocus) {
      var args = Array.prototype.slice.call(arguments);
      if (elementToFocus && elementToFocus.closest && elementToFocus.closest('.aps-protection-line')) {
        var scope = container && container.querySelector ? container : doc;
        args[1] = scope.querySelector('.cart-item:not(.aps-protection-line) .cart-item__name') || container;
      }
      return original.apply(this, args);
    };
    wrapped.__apsProtection = true;
    win.trapFocus = wrapped;
    return true;
  }

  function subscribeDawn(ctl) {
    try {
      // pubsub.js and constants.js are Dawn's own scripts; their names are global.
      /* global subscribe, PUB_SUB_EVENTS */
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
          if (event && event.source === 'aps-protection') return;
          ctl.schedule('dawn');
        });
        return true;
      }
    } catch (e) {
      /* Dawn's pub/sub is not on this page */
    }
    return false;
  }

  function watchDrawer(win, doc, ctl) {
    var drawer = doc.querySelector('cart-drawer');
    if (!drawer || typeof win.MutationObserver !== 'function') return;
    var wasOpen = drawer.classList.contains('active');
    new win.MutationObserver(function () {
      var open = drawer.classList.contains('active');
      if (open && !wasOpen) ctl.schedule('drawer-open');
      wasOpen = open;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  function listenForSwitch(doc, ui, ctl) {
    // Capture phase, so Dawn's quantity handlers never see this checkbox.
    doc.addEventListener(
      'change',
      function (event) {
        var input = event.target;
        if (!input || !input.matches || !input.matches('[data-aps-protection-input]')) return;
        event.stopPropagation();
        var on = !!input.checked;
        ui.showChoice(on);
        ctl.setDeclined(!on);
      },
      true
    );
  }

  /** If the customer clicks Check out while the protection line is being updated, wait (max 4 s) and then continue. */
  function guardCheckout(win, doc, ctl) {
    var bypass = false;
    doc.addEventListener(
      'click',
      function (event) {
        if (bypass || ctl.isIdle()) return;
        var btn = event.target && event.target.closest && event.target.closest('button[name="checkout"], input[name="checkout"]');
        if (!btn) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        var id = btn.id;
        ctl.whenIdle(CHECKOUT_WAIT_MS).then(function () {
          var again = (id && doc.getElementById(id)) || btn;
          if (!again.isConnected) {
            win.location.assign(shopRoot(win) + 'checkout');
            return;
          }
          bypass = true;
          try {
            again.click();
          } finally {
            bypass = false;
          }
        });
      },
      true
    );
  }

  function start(win, doc) {
    var cfg = readConfig(doc);
    if (!cfg) return null; // snippet not on this page (or the product is missing): nothing to manage
    var ui = createDomUI(win, doc, cfg);
    var ctl = createController({
      config: cfg,
      fetch: function (url, init) {
        return win.fetch(url, init);
      },
      root: shopRoot(win),
      sectionIds: ui.sectionIds,
      sectionsUrl: function () {
        return win.location.pathname;
      },
      render: ui.render,
      busy: ui.busy,
      hold: ui.hold,
      now: function () {
        return Date.now();
      },
      setTimeout: function (fn, ms) {
        return win.setTimeout(fn, ms);
      },
      clearTimeout: function (id) {
        win.clearTimeout(id);
      },
      warn: warn,
    });
    installFetchHook(win, ctl);
    installXhrHook(win, ctl);
    subscribeDawn(ctl);
    watchDrawer(win, doc, ctl);
    listenForSwitch(doc, ui, ctl);
    guardCheckout(win, doc, ctl);
    patchTrapFocus(win, doc);
    win.addEventListener('pageshow', function (event) {
      if (event.persisted) ctl.schedule('pageshow');
    });
    if (cfg.cartCount !== 0) ctl.schedule('load');
    return ctl;
  }

  function boot(win, doc) {
    if (win.__apsProtectionBooted) return;
    win.__apsProtectionBooted = true;
    var go = function () {
      try {
        api.controller = start(win, doc);
      } catch (e) {
        warn('could not start', e);
      }
    };
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', go);
    else go();
  }

  var api = {
    version: VERSION,
    controller: null,
    boot: boot,
    // exposed for the tests in _work/test
    normalizeConfig: normalizeConfig,
    pickVariant: pickVariant,
    summarize: summarize,
    cartValue: cartValue,
    decide: decide,
    signature: signature,
    patchTrapFocus: patchTrapFocus,
    isProtectionItem: isProtectionItem,
    formatMoney: formatMoney,
    isCartMutationUrl: isCartMutationUrl,
    isOwnRequest: isOwnRequest,
    installFetchHook: installFetchHook,
    installXhrHook: installXhrHook,
    createGuard: createGuard,
    createController: createController,
    constants: {
      OWN_HEADER: OWN_HEADER,
      DEBOUNCE_MS: DEBOUNCE_MS,
      MAX_WAIT_MS: MAX_WAIT_MS,
      MAX_STEPS: MAX_STEPS,
      GUARD_WINDOW_MS: GUARD_WINDOW_MS,
      GUARD_MAX: GUARD_MAX,
      GUARD_MAX_SAME: GUARD_MAX_SAME,
    },
  };
  return api;
});
